import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { motion } from "framer-motion";

import FnrButton from "UI/FnrButton3/FnrButton";
import FnrIcon from "UI/FnrIcon";
import { colors } from "UI/colors";

import { colorIsLight, lightenDarkenColor } from "Utilities/Colors/colorUtils";

import PaymentTypeHandler from "../../PaymentTypeHandler";

export default function Fund({
  name,
  ID,
  fundColor,
  paymentType,
  onClick,
  children,
}) {
  const buttonRef = useRef();

  const handleGiveButtonClick = () => {
    if (onClick) onClick();
    else window.location = `/give/${ID}`;
  };

  const internalRef = useRef();
  const measurements = useRef();

  const [measured, setMeasured] = useState(false);
  const [tapping, setTapping] = useState(false);
  const [hovering, setHovering] = useState(false);

  const hoverStart = (e) => {
    if (!measurements.current) measureButton();
    setHovering(true);
  };
  const hoverEnd = () => setHovering(false);

  const hexColor = `#${fundColor}`;

  const hilightColor = lightenDarkenColor(hexColor, 18);
  const shineColor = lightenDarkenColor(hexColor, 40);

  const darkText = colorIsLight(lightenDarkenColor(hexColor, -40));

  const measureButton = () => {
    measurements.current = buttonRef.current.getBoundingClientRect();
    setMeasured(true);
  };

  const updatePointer = ({ offsetX, offsetY, ...rest }) => {
    buttonRef.current.style.setProperty(
      "--fnrButtonShineX",
      (offsetX / measurements.current.width) * 100 + "%"
    );
    buttonRef.current.style.setProperty(
      "--fnrButtonShineY",
      (offsetY / measurements.current.height) * 100 + "%"
    );
  };

  useEffect(() => {
    measureButton();
    const button = buttonRef.current;
    button.addEventListener("pointermove", updatePointer);
    button.addEventListener("mouseover", measureButton);
    return () => {
      button.removeEventListener("pointermove", updatePointer);
      button.removeEventListener("mouseover", measureButton);
    };
  }, []);

  const tapStart = () => setTapping(true);
  const tapEnd = () => setTapping(false);

  const styles = {
    "--fnrButtonShineX": "50%",
    "--fnrButtonShineY": "50%",
    "--fnrButtonBgColor": hexColor,
    "--fnrButtonHilightColor": hilightColor,
    "--fnrButtonShineColor": shineColor,
  };

  if (measured) {
    styles["--fnrButtonShineRadius"] =
      Math.max(80, measurements.current.width, measurements.current.height) *
        2 +
      "px";
  }

  return (
    <FundListItem
      ref={buttonRef}
      onHoverStart={hoverStart}
      onHoverEnd={hoverEnd}
      onTapStart={tapStart}
      onTapCancel={tapEnd}
      onTap={tapEnd}
      style={styles}
      onClick={handleGiveButtonClick}
    >
      <Shine />
      <Inside whileTap={{ scale: 0.96 }}>
        {children ? (
          children
        ) : (
          <>
            <FundName>{name}</FundName>
            <Action>
              {new PaymentTypeHandler(paymentType).asVerb(true)}
              <FnrIcon fas fa-chevron-double-right />
            </Action>
          </>
        )}
      </Inside>
    </FundListItem>
  );
}

const FundListItem = styled(motion.div)`
  position: relative;
  cursor: pointer;
  border-top: 2px solid rgba(255, 255, 255, 0);
  border-bottom: 2px solid rgba(255, 255, 255, 0);
  box-sizing: border-box;
  margin-top: -2px;
  transition: border-top 0.2s, border-bottom 0.2s;
  &:hover {
    border-top: 2px solid rgba(255, 255, 255, 0.1);
    border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  }
`;

const FundName = styled.div`
  font-weight: 400;
  color: ${({ color }) => (color ? `#${color}` : "inherit")};
  font-size: 20px;
  max-width: 260px;
`;
const Inside = styled(motion.div)`
  padding: 8px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  position: relative;
  z-index: 10;
  text-shadow: rgba(0, 0, 0, 0) 1px 1px 4px;
  transition: text-shadow 0.2s;
  ${FundListItem}:hover & {
    text-shadow: rgba(0, 0, 0, 0.4) 1px 1px 4px;
  }
  &::after {
    content: "";
    position: absolute;
    background: rgba(255, 255, 255, 0);
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
`;
const Shine = styled.div`
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: 0;
  right: 0;
  background: radial-gradient(
    var(--fnrButtonShineRadius) circle at var(--fnrButtonShineX)
      var(--fnrButtonShineY),
    var(--fnrButtonShineColor),
    transparent 60%
  );
  /* dumb old safari */
  @supports not (aspect-ratio: auto) {
    background: radial-gradient(
      calc(var(--fnrButtonShineRadius) * 5) circle at var(--fnrButtonShineX)
        var(--fnrButtonShineY),
      var(--fnrButtonShineColor),
      transparent 60%
    );
  }
  opacity: 0;
  transition: opacity 0.8s, background 0.2s;
  z-index: 3;

  ${FundListItem}:not(.loading):not(.disabled):hover & {
    opacity: 0.3;
    transition: opacity 0.4s, background 0.2s;
  }
`;

const Action = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  opacity: 0;
  transform: scale(0.8);
  transition: 0.2s;

  ${FundListItem}:hover & {
    transform: none;
    opacity: 1;
  }
`;
